import { tabsListVariants, tabsTriggerVariants } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import { Link } from '@tanstack/react-router'
import {
  useLayoutEffect,
  useRef,
  type AriaAttributes,
  type ComponentType,
  type ReactNode,
} from 'react'

export type PageNavigationTabItem = {
  key: string
  label: ReactNode
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: AriaAttributes['aria-hidden'] }>
  isActive?: boolean
  link: AppLinkProps
}

export type PageNavigationTabsProps = {
  ariaLabel?: string
  items: PageNavigationTabItem[]
  className?: string
}

export function PageNavigationTabs({ ariaLabel, items, className }: PageNavigationTabsProps) {
  const activeTabRef = useRef<HTMLAnchorElement>(null)
  const activeKey = items.find((item) => item.isActive)?.key

  useLayoutEffect(() => {
    if (typeof activeTabRef.current?.scrollIntoView === 'function') {
      activeTabRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }, [activeKey])

  if (items.length <= 1) {
    return null
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={cn('min-w-0 overflow-x-auto overflow-y-hidden pretty-scrollbar', className)}
    >
      <div className={cn(tabsListVariants({ size: 'comfortable' }), 'min-w-max')}>
        {items.map(({ key, label, icon: Icon, isActive, link }) => (
          <Link
            key={key}
            ref={isActive ? activeTabRef : undefined}
            {...link}
            className={cn(
              tabsTriggerVariants({ size: 'comfortable' }),
              isActive && 'border-emphasis text-foreground'
            )}
            aria-current={isActive ? 'page' : undefined}
          >
            {Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
            {label}
          </Link>
        ))}
      </div>
    </nav>
  )
}
